import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, tap, catchError, of } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { ENV_TOKEN } from '../app.config';

export interface AuthResponse {
  success: boolean;
  message: string;
  csrf_token: string;
}

interface QueryParams {
  redirect: string | null;
}

export const HOST = 'myceliumllc.dev'as const;

export function domainForEnv(subdomain: string, env?: 'dev' | 'staging') {
  if(!env)
    return `${subdomain}.${HOST}`;

  return `${subdomain}-${env}.${HOST}`;
}

@Injectable({
  providedIn: 'root'
})
export class GateService {

  private env = inject<'dev' | 'staging'>(ENV_TOKEN);
  private document = inject(DOCUMENT);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);

  readonly gateHost = domainForEnv('gate', this.env);
  readonly gateEndpoint = `https://${this.gateHost}/v1/auth`;
  readonly redirectEndpoint = `https://${this.gateHost}/v1/auth/redirect`;
  readonly validateEndpoint = `https://${this.gateHost}/v1/auth/silent-auth`;
  readonly logoutEndpoint = `https://${this.gateHost}/v1/auth/logout`;
  readonly fortressHost = domainForEnv('fortress', this.env);
  defaultRedirect = 'https://' + this.fortressHost;

  private initialQueryParams?: QueryParams;

  private _loading = signal(true);
  loading = this._loading.asReadonly();

  constructor() {
    this.initialQueryParams = {
      redirect: new URLSearchParams(document.location.search).get('redirect')
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {}
    });

    if(this.router.url !== '/logout') {
      this.validate();
    }
  }

  async validate() {
    await lastValueFrom(this.http.get<{ authenticated: boolean, reason: string }>(`${this.validateEndpoint}`, { withCredentials: true } ).pipe(
      tap(response => {
        if(response.authenticated)
          this.redirect();
      })
    ));
  }

  async loginWithGoogle(token: string) {
    this._loading.set(true);
    try {
      await lastValueFrom(this.http.post<AuthResponse>(`${this.gateEndpoint}/google`, { token }, { withCredentials: true } ).pipe(
        tap(response => this.handleAuthResponse(response)),
      ));
      this.redirect();
    } catch (error) {
      this._loading.set(false);
      throw error;
    }
  }

  async redirect() {
    const redirect = this.initialQueryParams?.redirect ?? this.defaultRedirect;
    const url = this.redirectEndpoint + '?redirect_url=' + redirect;
    this.document.location.href = url;
  }

  private handleAuthResponse(response: AuthResponse): void {
    console.log(response);
    const { success, message, csrf_token } = response;

    if(!success)
      throw new Error(message);

    this._loading.set(false);
  }

}
